import { ContactDataType, jsHooks } from "@swo/shared";

import { Wrapper } from "./shared-components/wrapper";
import cn from "classnames";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import React from "react";
import { fontColor, FONTS, fontSize, fontStyles } from "../../../styles/fonts";
import { css } from "@emotion/react";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import { TickSvg } from "../../../assets";
import { CustomerServiceContactData } from "../../../components/_shared-components/CustomerServiceContactData";
import { usePageContext } from "../../../global/page-context";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import * as Icon from "./icons";
import { CSS_MQ } from "../../../styles/breakpoints";
import type { PurchaseConfirmationPageTranslation } from "@swo/storefront";
import { T } from "../../../components/T";
import type { Translated } from "../../../domain/translations/translations";

export const PurchaseConfirmationPageSuccessScreen: React.FC<
  React.PropsWithChildren<{
    t: Translated<PurchaseConfirmationPageTranslation>;
    homepageLink: string;
    context: {
      orderId: string;
      orderNumber: string;
      paymentId: string;
    };
    paymentMethodName: string;
    customerFullName: string;
    orderValue: string;
    paymentStatusValue: "pending" | "confirmed";
    contactData: ContactDataType;
  }>
> = ({ t, ...props }) => {
  const pageCtx = usePageContext();

  const labels = {
    customerName: props.customerFullName,
    blocks: [
      { label: t("orderNumber"), value: props.context.orderNumber },
      { label: t("paymentMethod"), value: props.paymentMethodName },
      {
        label: t("paymentStatus.label"),
        value: props.paymentStatusValue === "confirmed" ? t("paymentStatus.success") : t("paymentStatus.pending"),
      },
      { label: t("orderTotalValue"), value: props.orderValue },
    ],
    whatsNext: {
      headline: t("whatsNext.headline"),
      elements: [
        {
          headline: t("whatsNext.option1.headline"),
          description: t("whatsNext.option1.description"),
          Icon: Icon.IconOne,
        },
        {
          headline: t("whatsNext.option2.headline"),
          description: t("whatsNext.option2.description"),
          Icon: Icon.IconTwo,
        },
        {
          headline: t("whatsNext.option3.headline"),
          description: t("whatsNext.option3.description"),
          Icon: Icon.IconThree,
        },
        {
          headline: t("whatsNext.option4.headline"),
          description: t("whatsNext.option4.description"),
          Icon: Icon.IconFour,
        },
      ].filter(e => !!(e.headline || e.description)),
    },
  };

  return (
    <Wrapper className={cn("expand fill-screen", "align-start")} context={props.context}>
      <ContentContainer>
        <div
          css={[
            fontColor.green,
            css`
              text-align: center;
              margin-bottom: ${CSS_SPACER_SIZES.md};
            `,
          ]}
        >
          <div
            css={css`
              margin-left: auto;
              margin-right: auto;

              width: 48px;
              height: 48px;

              svg {
                width: 50%;
                height: 50%;
              }
              background-color: ${CSS_COLORS.Green};
              color: ${CSS_COLORS.White};
              border-radius: 100%;

              margin-bottom: 20px;
            `}
            className={"u-d-flex u-jc-center u-ai-center"}
          >
            <TickSvg />
          </div>

          <span
            css={[FONTS.MinervaModern500, fontSize.x1]}
            className={cn("u-uppercase u-d-block", jsHooks.purchaseConfirmationPage.success.headline)}
          >
            <T>{t("thankYouForYourOrderText")}</T>
          </span>
          <span
            css={[FONTS.SwarovskiFutura300, fontSize.x1]}
            className={cn("u-uppercase u-d-block", jsHooks.purchaseConfirmationPage.success.customerName)}
          >
            <T>{labels.customerName}</T>
          </span>
        </div>

        <div
          css={css`
            display: grid;
            gap: 16px;

            width: 100%;
            ${CSS_MQ.atLeastSm} {
              width: var(--max-narrow-content-width);
            }

            grid-template-columns: repeat(2, minmax(0, 1fr));
            ${CSS_MQ.atLeastMd} {
              grid-template-columns: repeat(4, minmax(0, 1fr));
            }

            margin-left: auto;
            margin-right: auto;

            margin-bottom: ${CSS_SPACER_SIZES.md};
            ${CSS_MQ.atLeastSm} {
              margin-bottom: ${CSS_SPACER_SIZES.lg};
            }
          `}
        >
          {labels.blocks.map(b => (
            <div
              key={b.label.value}
              className={"u-d-flex u-fd-column"}
              css={css`
                ${CSS_MQ.atLeastSm} {
                  align-items: center;
                }
              `}
            >
              <span
                css={[
                  FONTS.SwarovskiFutura300,
                  fontSize.x2,
                  css`
                    margin-bottom: 4px;
                    text-align: center;
                  `,
                ]}
                className={"u-d-block"}
              >
                <T>{b.label}</T>
              </span>
              <span
                css={[
                  FONTS.SwarovskiFutura500,
                  fontSize.x2,
                  css`
                    text-align: center;
                  `,
                ]}
                className={"u-uppercase"}
              >
                <T>{b.value}</T>
              </span>
            </div>
          ))}
        </div>

        <div
          css={css`
            max-width: 520px;
            margin-left: auto;
            margin-right: auto;
          `}
        >
          <div
            className={"u-ta-center"}
            css={css`
              margin-bottom: 30px;
              ${CSS_MQ.atLeastSm} {
                margin-bottom: 50px;
              }
            `}
          >
            <span css={[fontStyles.headline1]}>
              <T>{labels.whatsNext.headline}</T>
            </span>
          </div>

          <div>
            {labels.whatsNext.elements.map(el => {
              return (
                <div
                  key={el.headline.value}
                  css={css`
                    display: grid;
                    grid-template-columns: 64px auto;
                    grid-column-gap: 20px;

                    margin-bottom: 42px;
                  `}
                >
                  <div className={"u-d-flex u-jc-center u-ai-center"}>
                    <el.Icon
                      css={css`
                        width: 100%;
                      `}
                    />
                  </div>
                  <div>
                    <span
                      css={[
                        fontStyles.headline5,
                        css`
                          margin-bottom: 6px;
                        `,
                      ]}
                    >
                      <T>{el.headline}</T>
                    </span>
                    <p
                      css={[
                        FONTS.SwarovskiFutura300,
                        fontSize.x2,
                        css`
                          margin: 0;
                        `,
                      ]}
                    >
                      <T>{el.description}</T>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ContentContainer>

      {props.children ? (
        <>
          <Spacer size={"md"} />
          {props.children}
          <Spacer size={"md"} />
        </>
      ) : null}

      <ContentContainer>
        <hr
          css={css`
            margin: 16px 0;
            width: 100%;

            height: 1px;

            border: none;
            background-color: ${CSS_COLORS.Grey2};
          `}
        />

        <CustomerServiceContactData
          noSpacing={false}
          contactData={props.contactData}
          headline={undefined}
          chatbotEnabled={pageCtx.pageBase.locale.chatbotEnabled}
        />
      </ContentContainer>

      <Spacer size={"md"} />
    </Wrapper>
  );
};
